import type {
  AppApiClient,
  CalendarEvent,
  CalendarEventsResult,
  CheckAccessCodeInput,
  CheckAccessCodeResponse,
  EventsAiSummaryResult,
  ForgotPasswordInput,
  GetInviterResponse,
  GoogleCalendarsResult,
  GoogleLoginInput,
  GoogleLoginResult,
  GoogleOAuthStatus,
  JoinWaitlistInput,
  JoinWaitlistResponse,
  ListAccessCodesResponse,
  OnboardingProfile,
  PathwaySignupInput,
  PathwaySignupResponse,
  ResendVerificationCodeInput,
  ResetPasswordInput,
  ResolvePathwayTokenResponse,
  SaveSearchCriteriaResult,
  SearchCriteria,
  SearchCriteriaInput,
  SignInInput,
  SignUpInput,
  SuggestedEventsResult,
  UserGoals,
  OutreachStats,
  VerifyEmailInput,
} from "@/shared/api/contracts";
import type { AppBootstrap, AppSession, UserProfile } from "@/shared/types/app";
import { ApiError, httpClient } from "@/shared/api/httpClient";
import { clearClientFlow } from "@/shared/api/sessionStorage";
import { realOnboardingApi } from "@/shared/api/real/realOnboardingApi";
import { realDashboardApi } from "@/shared/api/real/realDashboardApi";
import { realNetworkApi } from "@/shared/api/real/realNetworkApi";
import { realPathwaysApi } from "@/shared/api/real/realPathwaysApi";
import { realJobsApi, realTrackerApi } from "@/shared/api/real/realTrackerApi";
import { realActiveSearchTargetsApi } from "@/shared/api/real/realActiveSearchTargetsApi";
import { realExtensionApi } from "@/shared/api/real/realExtensionApi";
import { realDiscoverApi } from "@/shared/api/real/realDiscoverApi";

// Shape returned by GET /api/auth/me on the Liminl NestJS backend.
interface BackendAuthUser {
  id: string;
  email: string;
  fullName: string | null;
  status?: string;
  createdAt?: string;
}

interface MeResponse {
  user: BackendAuthUser;
  hasCompletedOnboarding: boolean;
  hasSubmittedReferences?: boolean;
  emailVerified: boolean;
}

interface LoginResponse {
  userId: string;
  expiresAt: string;
  hasCompletedOnboarding?: boolean;
}

// Shape returned by POST /api/auth/google-login.
interface GoogleLoginResponse {
  userId: string;
  expiresAt: string;
  hasCompletedOnboarding: boolean;
}

interface RegisterResponse {
  userId: string;
  expiresAt: string;
  codeIssued?: string;
  usesRemaining?: number;
  maxUses?: number;
}

function displayNameFromBackend(user: BackendAuthUser): string {
  if (user.fullName && user.fullName.trim().length > 0) return user.fullName;
  const localPart = user.email.split("@")[0] ?? user.email;
  return localPart;
}

function meToSession(me: MeResponse): AppSession {
  return {
    user: {
      id: me.user.id,
      email: me.user.email,
      displayName: displayNameFromBackend(me.user),
      emailVerified: me.emailVerified,
    },
    // /me doesn't return expiresAt; signIn/signUp set it explicitly when they have it.
    expiresAt: null,
  };
}

function meToProfile(me: MeResponse): UserProfile {
  return {
    id: me.user.id,
    displayName: displayNameFromBackend(me.user),
    // Backend only signals boolean; we map to the contract's tri-state.
    // "in_progress" would require probing /api/onboarding/goals - punted until needed.
    onboardingStatus: me.hasCompletedOnboarding ? "complete" : "not_started",
    hasSubmittedReferences: Boolean(me.hasSubmittedReferences),
  };
}

async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await httpClient.get<MeResponse>("/api/auth/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return null;
    }
    throw err;
  }
}

// The calendar-events endpoint is backend-owned and may return either a bare
// array or a `{ events: [...] }` envelope, with each event either flat or in
// the Google-native nested shape (start/end as { dateTime | date }, attendees
// as an array). We normalize defensively so the UI never has to guess. If the
// backend contract is confirmed, this can be tightened.
function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function normalizeCalendarEvent(raw: unknown, index: number): CalendarEvent {
  const e = (raw ?? {}) as Record<string, unknown>;

  // start/end may be a plain ISO string, or Google's { dateTime, date }.
  const readPoint = (point: unknown): { value: string | null; allDay: boolean } => {
    if (typeof point === "string") return { value: asString(point), allDay: false };
    if (point && typeof point === "object") {
      const p = point as Record<string, unknown>;
      const dateTime = asString(p.dateTime);
      if (dateTime) return { value: dateTime, allDay: false };
      const date = asString(p.date);
      if (date) return { value: date, allDay: true };
    }
    return { value: null, allDay: false };
  };

  const start = readPoint(e.start ?? e.startTime ?? e.startsAt);
  const end = readPoint(e.end ?? e.endTime ?? e.endsAt);

  // attendeeCount may be provided directly, or derived from an attendees array.
  let attendeeCount = 0;
  if (typeof e.attendeeCount === "number") {
    attendeeCount = e.attendeeCount;
  } else if (Array.isArray(e.attendees)) {
    attendeeCount = e.attendees.length;
  }

  const readCount = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) ? value : null;

  const statusRaw = asString(e.status);
  const status =
    statusRaw === "confirmed" || statusRaw === "tentative" || statusRaw === "cancelled"
      ? statusRaw
      : null;

  return {
    id: asString(e.id) ?? asString(e.eventId) ?? `event-${index}`,
    title: asString(e.title) ?? asString(e.summary) ?? "Untitled event",
    start: start.value,
    end: end.value,
    attendeeCount,
    location: asString(e.location),
    allDay: start.allDay || end.allDay,
    htmlLink: asString(e.htmlLink),
    joinLink: asString(e.joinLink) ?? asString(e.hangoutLink),
    status,
    isRecurring: e.isRecurring === true || Boolean(asString(e.recurringEventId)),
    isOrganizer: e.isOrganizer === true,
    acceptedCount: readCount(e.acceptedCount),
    pendingCount: readCount(e.pendingCount),
    calendarId: asString(e.calendarId),
    calendarSummary: asString(e.calendarSummary),
    calendarPrimary: e.calendarPrimary === true || asString(e.calendarId) === "primary",
  };
}

function normalizeCalendarEvents(raw: unknown): CalendarEvent[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object" && Array.isArray((raw as { events?: unknown }).events)
      ? (raw as { events: unknown[] }).events
      : [];
  return list.map((item, index) => normalizeCalendarEvent(item, index));
}


export const realApiClient: AppApiClient = {
  onboarding: realOnboardingApi,
  dashboard: realDashboardApi,
  network: realNetworkApi,
  pathways: realPathwaysApi,
  jobs: realJobsApi,
  tracker: realTrackerApi,
  activeSearchTargets: realActiveSearchTargetsApi,
  extension: realExtensionApi,
  discover: realDiscoverApi,

  async getBootstrap(): Promise<AppBootstrap> {
    const me = await fetchMe();
    if (!me) return { session: null, profile: null };
    return { session: meToSession(me), profile: meToProfile(me) };
  },

  async getOnboardingProfile(): Promise<OnboardingProfile | null> {
    try {
      return await httpClient.get<OnboardingProfile | null>("/api/onboarding/profile");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  async saveOnboardingProfile(
    patch: Partial<Pick<OnboardingProfile, "experienceSummary" | "portfolioUrl" | "linkedinUrl">>,
  ): Promise<OnboardingProfile> {
    return httpClient.patch<OnboardingProfile>("/api/onboarding/profile", {
      experience_summary: patch.experienceSummary,
      portfolio_url: patch.portfolioUrl,
      linkedin_url: patch.linkedinUrl,
    });
  },

  async getGoals(): Promise<UserGoals> {
    try {
      return await httpClient.get<UserGoals>("/api/onboarding/goals");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return {};
      throw err;
    }
  },

  async getOutreachStats(): Promise<OutreachStats> {
    return httpClient.get<OutreachStats>("/api/outreach/stats");
  },

  async saveGoals(partial: Partial<UserGoals>): Promise<UserGoals> {
    // Merge-on-write: fetch current goals, apply partial, PUT full shape.
    let current: UserGoals = {};
    try {
      current = await httpClient.get<UserGoals>("/api/onboarding/goals");
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) throw err;
    }
    const merged: UserGoals = { ...current, ...partial };
    return httpClient.put<UserGoals>("/api/onboarding/goals", merged as Record<string, unknown>);
  },

  async getSearchCriteria(): Promise<SearchCriteria | null> {
    try {
      return await httpClient.get<SearchCriteria | null>("/api/onboarding/search-criteria");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  async saveSearchCriteria(input: SearchCriteriaInput): Promise<SaveSearchCriteriaResult> {
    return httpClient.post<SaveSearchCriteriaResult>(
      "/api/onboarding/search-criteria",
      input as unknown as Record<string, unknown>,
    );
  },

  async startGoogleOAuth(returnTo?: string): Promise<{ authorizationUrl: string }> {
    const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
    return httpClient.get<{ authorizationUrl: string }>(`/api/auth/google/start${qs}`);
  },

  async getGoogleOAuthStatus() {
    return httpClient.get<GoogleOAuthStatus>("/api/auth/google/status");
  },

  async disconnectGoogleOAuth(): Promise<{ disconnected: boolean }> {
    return httpClient.post<{ disconnected: boolean }>("/api/auth/google/disconnect", {});
  },

  async getCalendarEvents(limit = 5): Promise<CalendarEventsResult> {
    const raw = await httpClient.get<unknown>(
      `/api/google/calendar/events?limit=${encodeURIComponent(String(limit))}`,
    );
    return { events: normalizeCalendarEvents(raw) };
  },

  async getGoogleCalendars(): Promise<GoogleCalendarsResult> {
    const raw = await httpClient.get<{ calendars?: unknown } | unknown[]>("/api/google/calendar/calendars");
    const list = Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object" && Array.isArray((raw as { calendars?: unknown }).calendars)
        ? ((raw as { calendars: unknown[] }).calendars)
        : [];
    return {
      calendars: list.map((item, index) => {
        const c = (item ?? {}) as Record<string, unknown>;
        const asStr = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : null);
        return {
          id: asStr(c.id) ?? `calendar-${index}`,
          summary: asStr(c.summary),
          primary: c.primary === true,
          selected: c.selected === true,
          accessRole: asStr(c.accessRole),
          backgroundColor: asStr(c.backgroundColor),
          foregroundColor: asStr(c.foregroundColor),
        };
      }),
    };
  },

  async getEventsAiSummary(): Promise<EventsAiSummaryResult> {
    return httpClient.post<EventsAiSummaryResult>("/api/google/calendar/ai-summary");
  },

  async getSuggestedEvents(): Promise<SuggestedEventsResult> {
    return httpClient.get<SuggestedEventsResult>("/api/events/suggested");
  },

  async signIn(input: SignInInput): Promise<AppSession> {
    const res = await httpClient.post<LoginResponse>("/api/auth/login", {
      email: input.email.trim().toLowerCase(),
      password: input.password,
    });
    const me = await fetchMe();
    if (!me) {
      // Should never happen - we just wrote the session - but bail loudly if it does.
      throw new Error("Sign-in succeeded but /api/auth/me did not return a user.");
    }
    return { ...meToSession(me), expiresAt: res.expiresAt };
  },

  async signUp(input: SignUpInput): Promise<AppSession> {
    const res = await httpClient.post<RegisterResponse>("/api/auth/register", {
      email: input.email.trim().toLowerCase(),
      password: input.password,
      fullName: input.fullName.trim(),
      inviteCode: input.inviteCode,
    });
    // Store issued code for post-signup access-codes page (best-effort).
    if (res.codeIssued) {
      try {
        sessionStorage.setItem("liminl:code_issued", res.codeIssued);
      } catch {
        // sessionStorage unavailable - non-fatal.
      }
    }
    const me = await fetchMe();
    if (!me) {
      throw new Error("Sign-up succeeded but /api/auth/me did not return a user.");
    }
    return { ...meToSession(me), expiresAt: res.expiresAt };
  },

  async signInWithGoogle(input: GoogleLoginInput): Promise<GoogleLoginResult> {
    // `credential` is the Google Identity Services ID-token JWT. The backend
    // verifies it, finds-or-creates the user, and sets the HttpOnly session cookie.
    const res = await httpClient.post<GoogleLoginResponse>("/api/auth/google-login", {
      credential: input.credential,
      ...(input.inviteCode ? { inviteCode: input.inviteCode } : {}),
    });
    const me = await fetchMe();
    if (!me) {
      throw new Error("Google sign-in succeeded but /api/auth/me did not return a user.");
    }
    return {
      session: { ...meToSession(me), expiresAt: res.expiresAt },
      hasCompletedOnboarding: res.hasCompletedOnboarding,
    };
  },

  async verifyEmail(input: VerifyEmailInput) {
    return httpClient.post<{ ok: boolean }>("/api/auth/verify-email", {
      email: input.email.trim().toLowerCase(),
      code: input.code.trim(),
    });
  },

  async resendVerificationCode(input: ResendVerificationCodeInput) {
    return httpClient.post<{ ok: boolean }>("/api/auth/resend-verification-code", {
      email: input.email.trim().toLowerCase(),
    });
  },

  async requestPasswordReset(input: ForgotPasswordInput) {
    return httpClient.post<{ ok: boolean }>("/api/auth/forgot-password", {
      email: input.email.trim().toLowerCase(),
    });
  },

  async resetPassword(input: ResetPasswordInput) {
    return httpClient.post<{ ok: boolean }>("/api/auth/reset-password", {
      token: input.token.trim(),
      password: input.password,
    });
  },

  async signOut(): Promise<void> {
    try {
      await httpClient.post("/api/auth/logout", {});
    } catch {
      // Best-effort; AuthProvider clears its in-memory state regardless.
    } finally {
      clearClientFlow();
    }
  },

  async checkAccessCode(input: CheckAccessCodeInput): Promise<CheckAccessCodeResponse> {
    return httpClient.post<CheckAccessCodeResponse>("/api/access-codes/redeem/check", {
      code: input.code,
      ...(input.email ? { email: input.email } : {}),
    });
  },

  async joinWaitlist(input: JoinWaitlistInput): Promise<JoinWaitlistResponse> {
    return httpClient.post<JoinWaitlistResponse>("/api/waitlist/join", {
      email: input.email,
      ...(input.name ? { name: input.name } : {}),
      ...(input.heardAbout ? { heardAbout: input.heardAbout } : {}),
      ...(input.heardAboutDetail ? { heardAboutDetail: input.heardAboutDetail } : {}),
      ...(input.location ? { location: input.location } : {}),
      ...(typeof input.activelyJobSearching === "boolean"
        ? { activelyJobSearching: input.activelyJobSearching }
        : {}),
      ...(input.source ? { source: input.source } : {}),
      ...(input.referredByUserId ? { referredByUserId: input.referredByUserId } : {}),
      ...(input.referredFrom ? { referredFrom: input.referredFrom } : {}),
    });
  },

  async listMyAccessCodes(): Promise<ListAccessCodesResponse> {
    return httpClient.get<ListAccessCodesResponse>("/api/me/access-codes");
  },

  async getMyInviter(): Promise<GetInviterResponse> {
    return httpClient.get<GetInviterResponse>("/api/me/inviter");
  },

  async resolvePathwayToken(token: string): Promise<ResolvePathwayTokenResponse> {
    return httpClient.get<ResolvePathwayTokenResponse>(
      `/api/access-codes/pathway-token/resolve?token=${encodeURIComponent(token)}`,
    );
  },

  async pathwaySignup(input: PathwaySignupInput): Promise<PathwaySignupResponse> {
    return httpClient.post<PathwaySignupResponse>("/api/access-codes/pathway-signup", {
      token: input.token,
      email: input.email.trim().toLowerCase(),
      password: input.password,
      firstName: input.firstName,
      lastName: input.lastName,
    });
  },
};
